/**
 * TranscriptionModal — transcrição (letra) do tema Joga Junto BB.
 *
 * Aberta pelo link "Transcrição" no <ThemePlayer/>. Existe sobretudo por
 * acessibilidade: usuários de leitor de tela (ou sem áudio) leem a letra do tema.
 * A letra é a mesma dentro e fora da campanha — muda só como o tema é nomeado.
 *
 * Mesmo padrão visual da CopaRegulamento: portal react-modal, tema escuro Copa.
 */

import React from 'react';
import Modal from 'react-modal';
import { registerInteractionCampaign } from 'services/actionlog.service';
import styles from './TranscriptionModal.module.scss';

interface TranscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Como o tema é nomeado nos rótulos — ver <ThemePlayer/>. */
  themeName: string;
  /** Registra a abertura no relatório da Copa. Desligue fora da campanha. */
  telemetry: boolean;
}

/**
 * Letra do tema, em seções → estrofes → versos. Estrutura preservada para que o
 * leitor de tela anuncie cada seção e cada verso com a quebra natural.
 */
const LYRICS: { label: string; stanzas: string[][] }[] = [
  {
    label: 'Intro',
    stanzas: [['Ôôô...', 'Vem pra roda!', 'Vem jogar junto!', 'Ôôô... BB!']],
  },
  {
    label: 'Verso',
    stanzas: [
      ['Todo dia tem alguém', 'fazendo o melhor acontecer.', 'É parceria, é atitude,', 'é vontade de crescer.'],
      ['Reconhecer é valorizar,', 'quem inspira faz brilhar.', 'É assim que o nosso time', 'faz o Banco evoluir.'],
    ],
  },
  {
    label: 'Pré-refrão',
    stanzas: [['Quem reconhece fortalece.', 'Quem fortalece inspira.', 'Quem inspira transforma.', 'E a festa já vai começar!']],
  },
  {
    label: 'Refrão',
    stanzas: [
      ['Joga Junto BB!', 'Vem reconhecer!', 'Quem faz a diferença', 'faz o Banco crescer!'],
      [
        'Joga Junto BB!',
        'Todo mundo vai cantar!',
        'Colaboração!',
        'Inovação!',
        'Protagonismo!',
        'Foco no Cliente!',
        'Liderança Integradora!',
        'É o nosso jeito de trabalhar!',
      ],
    ],
  },
  {
    label: 'Ponte',
    stanzas: [
      ['Aqui vale o respeito.', 'Vale a ética.', 'Vale a integridade.', 'Vale a confiança.'],
      ['Não tem espaço', 'pra bajulação,', 'nem favorecimento.'],
      ['Aqui a gente joga junto,', 'com transparência', 'e de coração.'],
    ],
  },
  {
    label: 'Refrão final',
    stanzas: [
      ['Joga! Joga! Joga Junto BB!', 'Vem reconhecer!', 'Vem inspirar!', 'Vem transformar!'],
      ['Joga! Joga! Joga Junto BB!', 'Somos um só time!', 'Somos Banco do Brasil!', 'Joga Junto BB!'],
    ],
  },
];

export function TranscriptionModal({
  isOpen,
  onClose,
  themeName,
  telemetry,
}: TranscriptionModalProps) {
  function handleAfterOpen() {
    if (!telemetry) return;
    registerInteractionCampaign('me', 'copa_interaction', { type: 'view_transcription' });
  }

  return (
    <Modal
      isOpen={isOpen}
      onAfterOpen={handleAfterOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      overlayClassName={styles.overlay}
      className={styles.modal}
      portalClassName="relative z-[1300]"
      contentLabel={`Transcrição do tema do ${themeName}`}
    >
      {/* Cabeçalho */}
      <div className={styles.header}>
        <div className={styles.headerStar} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="#fbfd00">
            <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3z" />
          </svg>
        </div>
        <div className={styles.headerContent}>
          <div className={styles.pill}>Transcrição da letra</div>
          <h1 className={styles.title}>Ouça o tema do {themeName}</h1>
          <p className={styles.subtitle}>Joga Junto BB</p>
        </div>
        <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Fechar transcrição">
          ✕
        </button>
      </div>

      {/* Corpo rolável — letra estruturada */}
      <div className={styles.body}>
        <h2 className={styles.songTitle}>Joga Junto BB</h2>

        {LYRICS.map(section => (
          <section className={styles.section} key={section.label} aria-label={section.label}>
            <h3 className={styles.sectionLabel}>{section.label}</h3>
            {section.stanzas.map((stanza, sIdx) => (
              <p className={styles.stanza} key={sIdx}>
                {stanza.map((line, lIdx) => (
                  <React.Fragment key={lIdx}>
                    {line}
                    {lIdx < stanza.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </p>
            ))}
          </section>
        ))}

        <div className={styles.actions}>
          <button className={styles.btnPrimary} onClick={onClose} type="button">
            Fechar
          </button>
        </div>
      </div>
    </Modal>
  );
}
